import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { formatDateTimeIST } from '../../utils/dateUtils';
import { formatMilli } from '../../utils/formatUtils';
import { 
  Download, Calendar, Search, Users, Loader2, Gift, ArrowRight, Database
} from 'lucide-react';
import { BackButton, PageSkeleton } from '../../components/common';

export const AdminReferralBonusPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getReferralBonusReport(startDate, endDate);
      setEntries(res.data.entries || []);
      setTotals(res.data.totals || {});
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied');
        navigate('/admin');
      } else {
        toast.error('Failed to load referral bonus report');
      }
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, navigate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleDownloadCSV = async () => {
    setCsvLoading(true);
    try {
      const res = await adminAPI.downloadReferralBonusCSV(startDate, endDate);
      
      const blob = new Blob([res.data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Downloaded ${res.data.total_records} records`);
    } catch (error) {
      toast.error('Failed to download CSV');
    } finally {
      setCsvLoading(false);
    }
  };

  const handleBackfill = async () => {
    if (!window.confirm(
      'This will update existing referral bonus records with missing referrer/referee details.\n\n' +
      'Records showing "N/A" will be populated with actual user information where available.\n\n' +
      'Continue?'
    )) {
      return;
    }
    
    setBackfillLoading(true);
    try {
      const res = await adminAPI.runReferralBonusBackfill();
      toast.success(`Backfill complete! ${res.data.updated_count} records updated`);
      fetchReport(); // Refresh the data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfillLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream pb-8" data-testid="admin-referral-bonus-page">
      {/* Header */}
      <div className="bg-white border-b border-border px-4 py-4 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackButton to="/admin" />
            <h1 className="font-serif text-xl text-navy font-semibold">Referral Bonus Report</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBackfill}
              disabled={backfillLoading}
              className="gap-2"
            >
              {backfillLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
              Fix N/A Data
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadCSV}
              disabled={csvLoading || entries.length === 0}
              className="gap-2"
            >
              {csvLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Filters */}
        <Card className="mb-6 border-gold/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Filter by Date Range
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button onClick={fetchReport} disabled={loading} className="w-full">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Apply Filter
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4 text-center">
              <Gift className="w-6 h-6 text-blue-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-blue-700">{(totals.total_millis_allocated || 0).toFixed(2)}</p>
              <p className="text-sm text-blue-600">Total Milli's Allocated</p>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 text-green-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-green-700">{totals.total_rewards_given || 0}</p>
              <p className="text-sm text-green-600">Total Rewards Given</p>
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="mb-6 bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-purple-600 mt-0.5" />
              <div>
                <p className="font-medium text-purple-700">Understanding Referral Bonuses</p>
                <p className="text-sm text-purple-600 mt-1">
                  When a new user signs up using a referral code and makes their first purchase, 
                  both the <strong>Referrer</strong> (who shared the code) and <strong>Referee</strong> (who used the code) 
                  receive bonus Milli's as a reward.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Table */}
        <Card className="border-gold/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-navy flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Referral Bonus Allocations
              </span>
              <span className="text-sm font-normal text-muted-foreground">
                {entries.length} records
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No referral bonuses found for the selected period</p>
              </div>
            ) : (
              <div className="space-y-3">
                {entries.map((entry, idx) => (
                  <div 
                    key={entry.transaction_id || idx} 
                    className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-purple-100 text-purple-700">
                          {entry.reason || 'Referral Bonus'}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatDateTimeIST(entry.created_at)}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">+{entry.milli_amount?.toFixed(2)} Milli's</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm">
                      {/* Referred By (Referrer) */}
                      <div className="flex-1 p-2 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-500 mb-1">Referred By</p>
                        <p className="font-medium text-blue-700">{entry.referrer_name || 'N/A'}</p>
                        <p className="text-xs text-blue-500">{entry.referrer_phone || 'N/A'}</p>
                      </div>
                      
                      {/* Arrow */}
                      <ArrowRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      
                      {/* Referred To (Referee) */}
                      <div className="flex-1 p-2 bg-green-50 rounded-lg">
                        <p className="text-xs text-green-500 mb-1">Referred To</p>
                        <p className="font-medium text-green-700">{entry.referee_name || 'N/A'}</p>
                        <p className="text-xs text-green-500">{entry.referee_phone || 'N/A'}</p>
                      </div>
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-2">
                      ID: {entry.transaction_id}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminReferralBonusPage;
